import {Box, Grid, Typography, Tabs, Tab} from '@mui/material';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import ArrTransferList, {
  KeyValue,
} from '../../common/arr-transfer-list/ArrTransferList';
import {ArrDrawer} from '../../common/arr-drawer/ArrDrawer';
import {WorkProductTypes, ExportSelectedNames} from './ExportData.types';
import './DataExportReport.scss';
import ArrTreeView from '../../common/arr-tree-view/ArrTreeView';
import {ExportList} from './ExportDataList/ExportList';
import {ArrToggle} from '../../common/arr-toggle/ArrToggle';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../src/assets/images/info-icon.svg';
import ArrButton from '../../common/arr-button/ArrButton';
import {useTranslation} from 'react-i18next';
import ArrowForward from '@mui/icons-material/ArrowForward';
import {ExportDataReportResponse} from '../../../api/types/reportingApi.type';
import {useRunReport} from '../../../hooks/reporting/useReporting';
import {useLocation} from 'react-router-dom';
import {ActionType, ToastType, SourceTypeCode} from '../../../shared/constants';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import ScrubCharacter from './ScrubCharacter/ScrubCharacter';
import {getScrubChar} from '../../../api/endpoints/reportingApi';
import {singularizeWorkProduct} from '../../../utils/reportUtils';
import ArrModal from '../../common/arr-modal/ArrModal';
import ArrRadio from '../../common/arr-radio/ArrRadio';
const TAG_GROUP_EXPORT_TYPE = 'Taggroup';

type OutputNode = {
  id: string;
  label: string;
  isGroup?: boolean;
  children: OutputNode[];
  disabled?: boolean;
};

type OutputGroup = {
  name: string;
  treedata: OutputNode[];
};

type FilterItem = {
  sourceName: string;
  source: string[] | string | number[] | number;
};

type fieldType = {
  id: string;
  name: string;
  label: string;
  fieldDefault: string;
  newLineDefault: string;
  textDefault: string;
}[];

type scrubtype = {
  specialCharId: string;
  specialCharName: string;
  specialCharLabel: string;
  replacementCharId: string;
  replacementCharName: string;
  replacementCharLabel: string;
}[];

export function convertWorkProducts(input: WorkProductTypes): {
  data: OutputGroup[];
  children: Record<string, string[]>;
} {
  const groups: OutputGroup[] = [];
  const leafChildren: Record<string, string[]> = {};
  Object.entries(input).forEach(([groupName, items]) => {
    const nodeMap: Record<string, OutputNode> = {};
    const roots: OutputNode[] = [];
    items.forEach((item) => {
      const label = item.name ?? item.text;
      if (!label) return;
      nodeMap[item.id] = {
        id: item.id.toString(),
        label,
        isGroup: item.isGroup,
        children: [],
      };
    });
    items.forEach((item) => {
      if (!nodeMap[item.id]) return;
      const node = nodeMap[item.id];
      if (item.parent === '#' || !item.parent) {
        roots.push(node);
      } else {
        const parentNode = nodeMap[item.parent];
        if (parentNode) {
          parentNode.children.push(node);
        }
      }
    });
    const leafIds: string[] = [];
    Object.values(nodeMap).forEach((n) => {
      if (!n.children || n.children.length === 0) {
        leafIds.push(n.id);
      }
    });
    // eslint-disable-next-line security/detect-object-injection
    leafChildren[groupName] = leafIds;
    groups.push({
      name: groupName,
      treedata: roots,
    });
  });
  return {
    data: groups,
    children: leafChildren,
  };
}

export function applySelectedToWorkProducts(
  groups: OutputGroup[],
  selected: ExportSelectedNames[]
): OutputGroup[] {
  const selectedMap: Record<string, Set<string>> = {};
  selected.forEach((item) => {
    if (item.source === 'workProduct' && item.workProductKey) {
      if (!selectedMap[item.workProductKey]) {
        selectedMap[item.workProductKey] = new Set();
      }
      selectedMap[item.workProductKey].add(String(item.id));
    }
  });
  const cloneAndApply = (
    nodes: OutputNode[],
    groupName: string,
    inheritedDisabled = false
  ): OutputNode[] => {
    // eslint-disable-next-line security/detect-object-injection
    const selectedIds = selectedMap[groupName] ?? new Set<string>();

    return nodes.map((node) => {
      // If this node is selected, then this node and everything below it should be disabled
      const isThisNodeSelected = selectedIds.has(node.id);
      const propagateDisable = groupName === 'Tags';
      const nextInheritedDisabled = propagateDisable
        ? inheritedDisabled || isThisNodeSelected
        : inheritedDisabled;
      const cloned: OutputNode = {
        ...node,
        children: cloneAndApply(
          node.children,
          groupName,
          nextInheritedDisabled
        ),
      };

      let disabled = false;

      if (nextInheritedDisabled) {
        disabled = true;
      } else if (cloned.children.length === 0) {
        // Leaf: disable if leaf itself is selected
        disabled = selectedIds.has(cloned.id);
      } else {
        disabled = cloned.children.every((child) => child.disabled === true);
      }
      if (disabled) {
        cloned.disabled = true;
      } else {
        // Ensure no stale disabled flag remains
        delete (cloned as any).disabled;
      }
      return cloned;
    });
  };
  return groups.map((g) => ({
    name: g.name,
    treedata: cloneAndApply(g.treedata, g.name),
  }));
}

export function getNewItems(
  actual: ExportSelectedNames[],
  draft: ExportSelectedNames[],
  exportOrder: string[]
): ExportSelectedNames[] {
  const actualIds = new Set(actual.map((a) => a.dataId));

  return draft.filter((item) => {
    const wasPreviouslyAdded = actualIds.has(item.dataId);
    const isCurrentlyDisplayed = exportOrder.includes(item.dataId);
    return !wasPreviouslyAdded || !isCurrentlyDisplayed;
  });
}

export function removeUnneededParents(
  groups: OutputGroup[],
  groupName: string,
  selectedIds: string[]
): string[] {
  const selectedSet = new Set(selectedIds);
  const result = new Set<string>();
  const group = groups.find((g) => g.name === groupName);
  if (!group) return Array.from(selectedSet);
  const nodeMap = new Map<string, OutputNode>();
  const traverse = (nodes: OutputNode[]) => {
    for (const node of nodes) {
      nodeMap.set(node.id, node);
      traverse(node.children);
    }
  };
  traverse(group.treedata);
  const hasSelectedDescendant = (nodeId: string): boolean => {
    const root = nodeMap.get(nodeId);
    if (!root) return false;
    const stack = [...root.children];
    while (stack.length) {
      const child = stack.pop()!;
      if (selectedSet.has(child.id)) return true;
      stack.push(...child.children);
    }
    return false;
  };
  for (const id of selectedSet) {
    const node = nodeMap.get(id);
    if (!node) {
      result.add(id);
      continue;
    }
    const keep = node.children.length === 0 || hasSelectedDescendant(id);
    if (keep) {
      result.add(id);
    }
  }
  return Array.from(result);
}

function a11yProps(index: number) {
  return {
    id: `export-tab-${index}`,
    'aria-controls': `export-tabpanel-${index}`,
  };
}

export const getSelectedParentNodesForGroup = (
  groups: OutputGroup[],
  groupName: string,
  selected: Record<string, string[] | null>
) => {
  const group = groups.find((g) => g.name === groupName);
  if (!group) return [];
  const roots = new Set(group.treedata.map((r) => r.id));
  const map = new Map<string, OutputNode>();
  const stack = [...group.treedata];
  while (stack.length) {
    const n = stack.pop()!;
    map.set(n.id, n);
    n.children.forEach((c) => stack.push(c));
  }
  const ids = selected[groupName] ?? [];
  return (ids ?? []).filter((id) => {
    const node = map.get(String(id));
    if (!node) return false;
    const isParent = (node.children?.length ?? 0) > 0;
    const isOutermost = roots.has(node.id);
    const isAllTags = (node.label ?? '').trim().toLowerCase() === 'all tags';
    return isParent && !isOutermost && !isAllTags;
  });
};

const isAllTagsSelectedForGroup = (
  groups: OutputGroup[],
  groupName: string,
  selected: Record<string, string[] | null>
) => {
  const group = groups.find((g) => g.name === groupName);
  if (!group) return false;
  const map = new Map<string, OutputNode>();
  const stack = [...group.treedata];
  while (stack.length) {
    const n = stack.pop()!;
    map.set(n.id, n);
    n.children.forEach((c) => stack.push(c));
  }
  const ids = selected[groupName] ?? [];
  return (ids ?? []).some((id) => {
    const node = map.get(String(id));
    if (!node) return false;
    return (node.label ?? '').trim().toLowerCase() === 'all tags';
  });
};

const getIdToNodeMapForGroup = (groups: OutputGroup[], groupName: string) => {
  const group = groups.find((g) => g.name === groupName);
  if (!group) return new Map<string, OutputNode>();
  const map = new Map<string, OutputNode>();
  const stack = [...group.treedata];
  while (stack.length) {
    const n = stack.pop()!;
    map.set(n.id, n);
    n.children.forEach((c) => stack.push(c));
  }
  return map;
};

const getLabelsForIds = (idToNode: Map<string, OutputNode>, ids: string[]) => {
  return ids
    .map((id) => idToNode.get(String(id))?.label)
    .filter((x): x is string => Boolean(x));
};

const ExportDataMain = ({
  data,
  filters,
  onRunStatusChange,
  isReportShared,
  isReset = false,
}: {
  data: ExportDataReportResponse;
  filters: any;
  onRunStatusChange?: (ran: boolean, savePayload?: any) => void;
  isReportShared?: boolean;
  isReset?: boolean;
}) => {
  const {t} = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [isToggleActive, setIsToggleActive] = useState(true);
  const [isToggleScrubActive, setIsToggleScrubActive] = useState(true);
  const [addedWorkProduct, setAddedWorkProduct] = useState<
    Record<string, string[] | null>
  >({});
  const location = useLocation();
  const {item} = location.state ?? {};
  const pathSegment = location.pathname.split('/')[2];
  const didInitFromExportFields = useRef(false);
  const [selectedMetadataList, setSelectedMetadataList] = useState<KeyValue[]>(
    []
  );
  const [selectedAISummary, setSelectedAISummary] = useState<KeyValue[]>([]);
  const [selectedWorkProduct, setSelectedWorkProduct] = useState<
    Record<string, string[] | null>
  >({});
  const [_selectedCombinedData, setSelectedCombinedData] = useState<{
    metadata: KeyValue[];
    workProduct: Record<string, string[] | null>;
    aIGeneratedSummary: KeyValue[];
  }>({metadata: [], workProduct: {}, aIGeneratedSummary: []});
  const [exportSelectedName, setExportSelectedName] = useState<
    ExportSelectedNames[]
  >([]);
  const [exportOrder, setExportOrder] = useState<string[]>([]);
  const [selectedWorkProductTotalItem, setSelectedWorkProductTotalItem] =
    useState(0);
  const [isRunningReport, setIsRunningReport] = useState(false);
  const {mutate: runReport} = useRunReport();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);
  const [scrubCharData, setScrubCharData] = useState<scrubtype>([]);
  const [showScrubDrawer, setShowScrubDrawer] = useState(false);
  const [fieldList, setFieldList] = useState<fieldType>([]);
  const [finalScrubCharData, setFinalScrubCharData] =
    useState<scrubtype>(scrubCharData);
  const [replaceFieldList, setReplaceFieldList] = useState<fieldType>([]);
  const [showTagModal, setShowTagModal] = useState(false);
  const [selectedTagGroupName, setSelectedTagGroupName] = useState('');
  const [tagGroupChoice, setTagGroupChoice] = useState('group');
  const [tagGroupQueue, setTagGroupQueue] = useState<string[]>([]);
  const [tagGroupQueueIndex, setTagGroupQueueIndex] = useState(0);
  const processedParentTagsRef = useRef<Set<string>>(new Set());
  const triggerToast = useCallback((message: string, status: ToastType[]) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  }, []);

  const replaceKey = <T extends Record<string, any>>(
    arr: T[],
    oldKey: string,
    newKey: string
  ) => {
    return arr.map((obj) => {
      if (!(oldKey in obj)) return obj;

      const {[oldKey]: value, ...rest} = obj;
      return {
        [newKey]: value,
        ...rest,
      } as T;
    });
  };

  useEffect(() => {
    const init = async () => {
      const payload = {
        scrubCount: 10,
      };
      try {
        const result = await getScrubChar(payload);
        if (Object.keys(result)?.length > 0) {
          setScrubCharData(result?.exportDataFormatModel?.scrubCharList);
          setFinalScrubCharData(result?.exportDataFormatModel?.scrubCharList);
          const result1 = replaceKey(
            result?.exportDataFormatModel?.fieldList,
            'name',
            'specialCharName'
          );
          setFieldList(result1);
          const result2 = replaceKey(
            result?.exportDataFormatModel?.fieldList,
            'name',
            'replacementCharName'
          );
          setReplaceFieldList(result2);
        }
      } catch (error) {
        console.error('Error initializing export view scrub', error);
      }
    };
    init();
  }, []);

  const metadataKeyValue = useMemo(() => {
    return (
      data?.exportModelData?.metaDataList?.map((item) => ({
        id: item.id.toString(),
        label: item.name,
      })) ?? []
    );
  }, [data]);

  const aiSummaryKeyValue = useMemo(() => {
    return (
      data?.exportModelData?.aiGeneratedSummaryTreeList?.map((item) => ({
        id: item.id,
        label: item.text,
      })) ?? []
    );
  }, [data]);

  const handleActionToggle = (status: boolean) => {
    setIsToggleActive(status);
  };
  const handleActionScrubToggle = (status: boolean) => {
    setIsToggleScrubActive(status);
  };
  const handleShowScrubDrawer = () => {
    setShowScrubDrawer(true);
  };
  const handleOnCancel = () => {
    setShowScrubDrawer(false);
  };
  const handleScrubListUpdate = (item: scrubtype) => {
    setFinalScrubCharData(item);
    setShowScrubDrawer(false);
  };
  const handleTabChange = (_e: React.SyntheticEvent, val: number) =>
    setActiveTab(val);

  const handleMetadataCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    item: KeyValue
  ) => {
    const isChecked = event.target.checked;
    setSelectedMetadataList((prev) =>
      isChecked
        ? prev.some((i) => i.id === item.id)
          ? prev
          : [...prev, item]
        : prev.filter((i) => i.id !== item.id)
    );
  };

  const handleAISummaryChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    item: KeyValue
  ) => {
    const isChecked = event.target.checked;
    setSelectedAISummary((prev) =>
      isChecked
        ? prev.some((i) => i.id === item.id)
          ? prev
          : [...prev, item]
        : prev.filter((i) => i.id !== item.id)
    );
  };

  const metadataWithDisabled = metadataKeyValue.map((item) => ({
    ...item,
    disabled: exportSelectedName.some((i) => i.dataId === `m-${item.id}`),
  }));

  const workProductData = useMemo(() => {
    if (!data?.exportModelData) return {};
    const m = data.exportModelData;
    const mapper = (list: any[] = []) =>
      list.map((x) => ({
        ...x,
        id: x.id.toString(),
        parent: x.parent?.toString(),
      }));
    return {
      Folders: mapper(m.folderTreeList),
      Tags: mapper(m.tagTreeList),
      'Redaction Tags': mapper(m.redactionTagTreeList),
      Comments: mapper(m.commentsTreeList),
      Production: mapper(m.productionsTreeList),
      Assignments: mapper(m.assignmentTreeList),
      //Scores: mapper(m.aiScoreTreeList),
    };
  }, [data]);

  const WORK_PRODUCT_TREE = convertWorkProducts(
    workProductData as WorkProductTypes
  );

  const handleWorkProductSelection = (key: string, values: string[] | null) => {
    setSelectedWorkProduct((prev) => ({...prev, [key]: values}));
  };

  useEffect(() => {
    let total = 0;
    const leafChildren = WORK_PRODUCT_TREE.children ?? {};
    Object.entries(selectedWorkProduct).forEach(([group, arr]) => {
      // eslint-disable-next-line security/detect-object-injection
      const leafSet = new Set(leafChildren[group] ?? []);
      const count = (arr ?? []).filter((id) =>
        leafSet.has(id.toString())
      ).length;
      total += count;
    });
    setSelectedWorkProductTotalItem(total);
  }, [selectedWorkProduct, WORK_PRODUCT_TREE.children]);

  const normalizeKey = (v: any) =>
    String(v ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  const getGroupKeyFromType = (type: any, groupKeys: string[]) => {
    const n = normalizeKey(type);
    const alias: Record<string, string> = {
      folder: 'Folders',
      folders: 'Folders',
      foldertree: 'Folders',
      foldertreelist: 'Folders',
      tag: 'Tags',
      tags: 'Tags',
      tagtree: 'Tags',
      tagtreelist: 'Tags',
      aiscore: 'Scores',
      redactiontag: 'Redaction Tags',
      redactiontags: 'Redaction Tags',
      redactiontagtree: 'Redaction Tags',
      redactiontagtreelist: 'Redaction Tags',
      comment: 'Comments',
      comments: 'Comments',
      commentstree: 'Comments',
      commentstreelist: 'Comments',
      production: 'Production',
      productions: 'Production',
      productiontree: 'Production',
      productionstreelist: 'Production',
    };
    // eslint-disable-next-line security/detect-object-injection
    const aliased = alias[n];
    if (aliased && groupKeys.includes(aliased)) return aliased;
    return (
      groupKeys.find((k) => normalizeKey(k) === n) ??
      groupKeys.find(
        (k) => normalizeKey(k).includes(n) || n.includes(normalizeKey(k))
      ) ??
      null
    );
  };

  const collectLeafDescendants = (
    groups: OutputGroup[],
    groupName: string,
    nodeId: string
  ): string[] => {
    const group = groups.find((g) => g.name === groupName);
    if (!group) return [];
    const find = (nodes: OutputNode[]): OutputNode | null => {
      for (const n of nodes) {
        if (n.id === nodeId) return n;
        const child = find(n.children ?? []);
        if (child) return child;
      }
      return null;
    };
    const root = find(group.treedata);
    if (!root) return [];
    const leaves: string[] = [];
    const walk = (n: OutputNode) => {
      if (!n.children || n.children.length === 0) {
        leaves.push(n.id);
        return;
      }
      n.children.forEach(walk);
    };
    walk(root);
    return leaves;
  };

  useEffect(() => {
    if (didInitFromExportFields.current) return;
    if (pathSegment !== 'export') return;

    const model: any = (data as any)?.exportModelData;
    const modelExportFields = Array.isArray(model)
      ? model?.[0]?.exportFields
      : model?.exportFields;

    const exportFields =
      (data as any)?.selectedSourceDetails?.exportFields ??
      (data as any)?.savedSelectionCriteria?.exportFields ??
      (data as any)?.selectionCriteria?.exportFields ??
      (data as any)?.exportFields ??
      modelExportFields ??
      null;

    if (!Array.isArray(exportFields) || exportFields.length === 0) return;

    const normalizedFields = [...exportFields].sort(
      (a: any, b: any) => (a?.index ?? 0) - (b?.index ?? 0)
    );

    const metaById = new Map<string, KeyValue>(
      metadataKeyValue.map((m) => [String(m.id), m])
    );

    const groupKeys = Object.keys(workProductData ?? {});
    const workProductLabelByGroupAndId = new Map<string, Map<string, string>>();
    groupKeys.forEach((g) => {
      // eslint-disable-next-line security/detect-object-injection
      const list: any[] = (workProductData as any)?.[g] ?? [];
      workProductLabelByGroupAndId.set(
        g,
        new Map<string, string>(
          list.map((x: any) => [
            String(x?.id),
            String(x?.text ?? x?.name ?? ''),
          ])
        )
      );
    });

    const leafSetByGroup = new Map<string, Set<string>>();
    Object.keys(WORK_PRODUCT_TREE.children ?? {}).forEach((g) => {
      // eslint-disable-next-line security/detect-object-injection
      leafSetByGroup.set(g, new Set(WORK_PRODUCT_TREE.children[g] ?? []));
    });

    const initialMetadata: KeyValue[] = [];
    const initialWorkProduct: Record<string, string[]> = {};
    const initialExportSelected: ExportSelectedNames[] = [];
    const initialOrder: string[] = [];

    const addSelectedExportItem = (
      dataId: string,
      entry: ExportSelectedNames
    ) => {
      if (!initialExportSelected.some((x) => x.dataId === dataId)) {
        initialExportSelected.push(entry);
        initialOrder.push(dataId);
      }
    };

    normalizedFields.forEach((f: any) => {
      const type = f?.type;
      const fieldId = f?.fieldId;

      if (normalizeKey(type) === 'metadata') {
        const id = String(fieldId);
        const meta = metaById.get(id);
        if (!meta) return;

        if (!initialMetadata.some((x) => String(x.id) === id)) {
          initialMetadata.push(meta);
        }

        const dataId = `m-${id}`;
        addSelectedExportItem(dataId, {
          id: meta.id,
          dataId,
          source: 'metadata',
          label: meta.label ?? '',
          prefix: 'Metadata',
        } as any);
        return;
      }

      if (normalizeKey(type) === normalizeKey(TAG_GROUP_EXPORT_TYPE)) {
        const parentId = String(fieldId);
        const tagLeafSet = leafSetByGroup.get('Tags') ?? new Set<string>();
        const leafIds = collectLeafDescendants(
          WORK_PRODUCT_TREE.data,
          'Tags',
          parentId
        ).filter((x) => tagLeafSet.has(String(x)));
        if (!leafIds.length) return;
        initialWorkProduct['Tags'] = initialWorkProduct['Tags'] ?? [];
        [parentId, ...leafIds].forEach((id) => {
          if (!initialWorkProduct['Tags'].includes(id)) {
            initialWorkProduct['Tags'].push(id);
          }
        });
        const label =
          workProductLabelByGroupAndId.get('Tags')?.get(parentId) ?? parentId;
        const dataId = `Tags-taggroup-${parentId}`;
        addSelectedExportItem(dataId, {
          id: parentId,
          dataId,
          source: 'workProduct',
          label,
          workProductKey: 'Tags',
          isParent: false,
          prefix: TAG_GROUP_EXPORT_TYPE,
        } as any);
        processedParentTagsRef.current.add(parentId);
        return;
      }

      const wpKey = getGroupKeyFromType(type, groupKeys);
      if (!wpKey) return;

      const rawId = String(fieldId);
      const leafSet = leafSetByGroup.get(wpKey) ?? new Set<string>();
      let leafIds: string[] = [];

      if (leafSet.has(rawId)) {
        leafIds = [rawId];
      } else {
        leafIds = collectLeafDescendants(
          WORK_PRODUCT_TREE.data,
          wpKey,
          rawId
        ).filter((x) => leafSet.has(String(x)));
      }

      if (!leafIds.length) return;

      // eslint-disable-next-line security/detect-object-injection
      initialWorkProduct[wpKey] = initialWorkProduct[wpKey] ?? [];

      leafIds.forEach((leafId) => {
        // eslint-disable-next-line security/detect-object-injection
        if (!initialWorkProduct[wpKey].includes(leafId)) {
          // eslint-disable-next-line security/detect-object-injection
          initialWorkProduct[wpKey].push(leafId);
        }

        const labelMap = workProductLabelByGroupAndId.get(wpKey);
        const label = labelMap?.get(String(leafId)) ?? '';
        if (!label) return;

        const dataId = `${wpKey}-${leafId}`;
        addSelectedExportItem(dataId, {
          id: leafId,
          dataId,
          source: 'workProduct',
          label,
          workProductKey: wpKey,
          isParent: false,
          prefix: wpKey,
        } as any);
      });
    });

    if (initialMetadata.length > 0) {
      setSelectedMetadataList(initialMetadata);
    }
    if (Object.keys(initialWorkProduct).length > 0) {
      setSelectedWorkProduct((prev) => ({...prev, ...initialWorkProduct}));
    }
    if (initialExportSelected.length > 0) {
      setExportSelectedName(initialExportSelected);
      setExportOrder(initialOrder);
    }

    didInitFromExportFields.current = true;
  }, [
    data,
    pathSegment,
    metadataKeyValue,
    workProductData,
    WORK_PRODUCT_TREE.data,
    WORK_PRODUCT_TREE.children,
  ]);

  const workProductTabDataWithDisabled = applySelectedToWorkProducts(
    WORK_PRODUCT_TREE.data,
    exportSelectedName
  );

  const treeOrderMap = useMemo(() => {
    const map = new Map<string, number>();
    let index = 0;

    const walk = (nodes: OutputNode[], group: string) => {
      nodes.forEach((node) => {
        map.set(`${group}-${node.id}`, index++);
        if (node.children?.length) {
          walk(node.children, group);
        }
      });
    };

    WORK_PRODUCT_TREE.data.forEach((group) => {
      walk(group.treedata, group.name);
    });

    return map;
  }, [WORK_PRODUCT_TREE.data]);
  const setModalForParentTag = (parentId: string) => {
    const idToNode = getIdToNodeMapForGroup(WORK_PRODUCT_TREE.data, 'Tags');
    const labels = getLabelsForIds(idToNode, [parentId]);
    setSelectedTagGroupName(labels[0] ?? '');
    setTagGroupChoice('group');
    setShowTagModal(true);
  };
  const clearTagModalQueue = () => {
    setTagGroupQueue([]);
    setTagGroupQueueIndex(0);
  };
  const handleTagModalCancel = () => {
    setShowTagModal(false);
    clearTagModalQueue();
  };

  const isParentTagAlreadyProcessed = (parentId: string) => {
    parentId = String(parentId);
    if (
      !exportSelectedName.some(
        (x) => x.dataId === `Tags-taggroup-${parentId}`
      ) &&
      exportSelectedName.length == 0
    ) {
      return false;
    }
    if (processedParentTagsRef.current.has(parentId)) {
      return true;
    }
    const tagLeafSet = new Set(WORK_PRODUCT_TREE.children?.['Tags'] ?? []);
    const leaves = collectLeafDescendants(
      WORK_PRODUCT_TREE.data,
      'Tags',
      parentId
    ).filter((id) => tagLeafSet.has(String(id)));
    if (leaves.length === 0) return false;
    const selectedLeafDataIds = new Set(
      exportSelectedName
        .filter(
          (x) =>
            x.workProductKey === 'Tags' &&
            !String(x.dataId).includes('taggroup-')
        )
        .map((x) => x.dataId)
    );
    return leaves.every((leafId) => selectedLeafDataIds.has(`Tags-${leafId}`));
  };
  const handleTagModalOk = () => {
    const currentParentId = tagGroupQueue[tagGroupQueueIndex];
    if (!currentParentId) {
      setShowTagModal(false);
      clearTagModalQueue();
      return;
    }
    const isLast = tagGroupQueueIndex >= tagGroupQueue.length - 1;
    if (tagGroupChoice === 'group') {
      addSelectedItems({
        parentTagsOverride: [currentParentId],
        closeTagModal: isLast,
      });
    } else {
      const tagLeafSet = new Set(WORK_PRODUCT_TREE.children?.['Tags'] ?? []);
      const leafIdsOnly = collectLeafDescendants(
        WORK_PRODUCT_TREE.data,
        'Tags',
        String(currentParentId)
      ).filter((id) => tagLeafSet.has(String(id)));
      addSelectedItems({
        parentTagsOverride: [currentParentId],
        tagLeafOverride: leafIdsOnly.map(String),
        forceTagIndividual: true,
        closeTagModal: isLast,
      });
    }
    processedParentTagsRef.current.add(String(currentParentId));
    if (isLast) {
      clearTagModalQueue();
      return;
    }
    const nextIndex = tagGroupQueueIndex + 1;
    setTagGroupQueueIndex(nextIndex);
    setModalForParentTag(tagGroupQueue[nextIndex]);
  };
  const handleAddToSelected = () => {
    const tagLeafSet = new Set(WORK_PRODUCT_TREE.children?.['Tags'] ?? []);
    const selectedTags = selectedWorkProduct['Tags'] ?? [];
    const isChildOnly =
      selectedTags.length > 0 &&
      selectedTags.every((id) => tagLeafSet.has(String(id)));

    if (isChildOnly) {
      addSelectedItems({parentTagsOverride: [], forceTagIndividual: true});
      return;
    }

    const parentTags = getSelectedParentNodesForGroup(
      WORK_PRODUCT_TREE.data,
      'Tags',
      selectedWorkProduct
    );
    const allTagsSelected = isAllTagsSelectedForGroup(
      WORK_PRODUCT_TREE.data,
      'Tags',
      selectedWorkProduct
    );
    if (parentTags.length > 0 && !allTagsSelected) {
      const allParentsAlreadyProcessed = parentTags.every((pid) =>
        isParentTagAlreadyProcessed(pid)
      );

      if (allParentsAlreadyProcessed) {
        // Leaves under a parent already exported as a tag group column must
        // not be re-added as individual tag columns.
        const groupedLeafIds = new Set<string>();
        parentTags
          .filter((pid) =>
            exportSelectedName.some((x) => x.dataId === `Tags-taggroup-${pid}`)
          )
          .forEach((pid) => {
            collectLeafDescendants(
              WORK_PRODUCT_TREE.data,
              'Tags',
              String(pid)
            ).forEach((leaf) => groupedLeafIds.add(String(leaf)));
          });
        const remainingLeafIds = (selectedWorkProduct['Tags'] ?? [])
          .map(String)
          .filter((id) => tagLeafSet.has(id) && !groupedLeafIds.has(id));
        if (remainingLeafIds.length > 0) {
          addSelectedItems({
            tagLeafOverride: remainingLeafIds,
            forceTagIndividual: true,
          });
        } else {
          addSelectedItems({excludeTags: true});
        }
        return;
      }

      addSelectedItems({excludeTags: true, closeTagModal: false});
      const tagIds = (selectedWorkProduct['Tags'] ?? []).map(String);
      const selectedLeafIds = tagIds.filter((id) => tagLeafSet.has(String(id)));
      const descendantLeafIds = new Set<string>();
      parentTags.forEach((pid) => {
        collectLeafDescendants(
          WORK_PRODUCT_TREE.data,
          'Tags',
          String(pid)
        ).forEach((leaf) => {
          if (tagLeafSet.has(String(leaf))) descendantLeafIds.add(String(leaf));
        });
      });
      const outsideParentLeafIds = selectedLeafIds.filter(
        (id) => !descendantLeafIds.has(String(id))
      );
      if (outsideParentLeafIds.length > 0) {
        addSelectedItems({
          tagLeafOverride: outsideParentLeafIds,
          forceTagIndividual: true,
          closeTagModal: false,
        });
      }
      const pendingParentTags = parentTags.filter(
        (pid) => !isParentTagAlreadyProcessed(pid)
      );
      if (pendingParentTags.length > 0) {
        setTagGroupQueue(pendingParentTags);
        setTagGroupQueueIndex(0);
        setModalForParentTag(pendingParentTags[0]);
      }
      return;
    }
    addSelectedItems();
  };
  const addSelectedItems = (options?: {
    tagLeafOverride?: string[];
    forceTagIndividual?: boolean;
    closeTagModal?: boolean;
    parentTagsOverride?: string[];
    excludeTags?: boolean;
  }) => {
    const parentTags =
      options?.parentTagsOverride ??
      getSelectedParentNodesForGroup(
        WORK_PRODUCT_TREE.data,
        'Tags',
        selectedWorkProduct
      );
    const allTagsSelected = isAllTagsSelectedForGroup(
      WORK_PRODUCT_TREE.data,
      'Tags',
      selectedWorkProduct
    );
    const tagLeafSet = new Set(WORK_PRODUCT_TREE.children?.['Tags'] ?? []);
    const tagLabelMap = new Map<string, string>(
      (workProductData['Tags'] ?? []).map((x: any) => [
        String(x.id),
        String(x?.text ?? x?.name ?? ''),
      ])
    );
    setSelectedCombinedData({
      metadata: selectedMetadataList,
      workProduct: selectedWorkProduct,
      aIGeneratedSummary: selectedAISummary,
    });
    setAddedWorkProduct(selectedWorkProduct);
    const draftExportList: ExportSelectedNames[] = [];
    selectedMetadataList.forEach((element) => {
      draftExportList.push({
        id: element.id,
        dataId: `m-${element.id}`,
        source: 'metadata',
        label: element.label ?? '',
        prefix: 'Metadata',
      });
    });
    Object.keys(selectedWorkProduct).forEach((key) => {
      // eslint-disable-next-line security/detect-object-injection
      if (options?.excludeTags && key === 'Tags') return;
      // eslint-disable-next-line security/detect-object-injection
      const product = selectedWorkProduct[key] ?? [];
      const filteredProductOnlyChildren = product.filter((id) =>
        // eslint-disable-next-line security/detect-object-injection
        WORK_PRODUCT_TREE.children[key].includes(id.toString())
      );
      if (product?.length > 0) {
        if (key === 'Tags') {
          if (allTagsSelected) {
            const allLeafIds = (
              WORK_PRODUCT_TREE.children?.['Tags'] ?? []
            ).slice();
            allLeafIds
              .sort(
                (a, b) =>
                  (treeOrderMap.get(`Tags-${a}`) ?? 9999) -
                  (treeOrderMap.get(`Tags-${b}`) ?? 9999)
              )
              .forEach((leafId) => {
                draftExportList.push({
                  id: leafId,
                  dataId: `Tags-${leafId}`,
                  source: 'workProduct',
                  label: tagLabelMap.get(String(leafId)) ?? '',
                  workProductKey: 'Tags',
                  isParent: false,
                  prefix: 'Tags',
                });
              });
          } else if (
            parentTags.length > 0 &&
            tagGroupChoice === 'group' &&
            !options?.forceTagIndividual
          ) {
            parentTags.forEach((pid) => {
              const parentLabel = tagLabelMap.get(String(pid)) ?? '';
              draftExportList.push({
                id: pid,
                dataId: `Tags-taggroup-${pid}`,
                source: 'workProduct',
                label: parentLabel || String(pid),
                workProductKey: 'Tags',
                isParent: false,
                prefix: TAG_GROUP_EXPORT_TYPE,
              });
            });
          } else {
            const selectedLeafIds = new Set<string>(
              (options?.tagLeafOverride?.length
                ? options.tagLeafOverride
                : product
              )
                .filter((id) => tagLeafSet.has(String(id)))
                .map(String)
            );
            if (
              !options?.tagLeafOverride?.length &&
              !options?.forceTagIndividual
            ) {
              parentTags.forEach((pid) => {
                collectLeafDescendants(
                  WORK_PRODUCT_TREE.data,
                  'Tags',
                  String(pid)
                ).forEach((leaf) => {
                  if (tagLeafSet.has(String(leaf)))
                    selectedLeafIds.add(String(leaf));
                });
              });
            }
            Array.from(selectedLeafIds)
              .sort(
                (a, b) =>
                  (treeOrderMap.get(`Tags-${a}`) ?? 9999) -
                  (treeOrderMap.get(`Tags-${b}`) ?? 9999)
              )
              .forEach((leafId) => {
                draftExportList.push({
                  id: leafId,
                  dataId: `Tags-${leafId}`,
                  source: 'workProduct',
                  label: tagLabelMap.get(String(leafId)) ?? '',
                  workProductKey: 'Tags',
                  isParent: false,
                  prefix: 'Tags',
                });
              });
          }
        } else {
          let filteredProduct = product;
          if (key === 'Production') {
            // Only export IDs that are leaf nodes in Production
            const leafSet = new Set(
              WORK_PRODUCT_TREE.children['Production'] ?? []
            );
            filteredProduct = product.filter((id) =>
              leafSet.has(id.toString())
            );
          }
          const selectedWorkProductKey = workProductData[
            key as keyof typeof workProductData
          ]
            ?.filter((item) => filteredProduct.includes(item.id.toString()))
            .sort((a, b) => {
              const aKey = `${key}-${a.id}`;
              const bKey = `${key}-${b.id}`;
              return (
                (treeOrderMap.get(aKey) ?? 9999) -
                (treeOrderMap.get(bKey) ?? 9999)
              );
            });
          selectedWorkProductKey?.forEach((element) => {
            draftExportList.push({
              id: element.id,
              dataId: `${key}-${element.id}`,
              source: 'workProduct',
              label: element?.text ?? element?.name ?? '',
              workProductKey: key,
              isParent: !filteredProductOnlyChildren.includes(
                element.id.toString()
              ),
              prefix: key,
            });
          });
        }
      }
    });

    selectedAISummary.forEach((element) => {
      draftExportList.push({
        id: element.id,
        dataId: `${element.id}`,
        source: 'aIGeneratedSummary',
        label: element?.label ?? element?.name ?? '',
      });
    });

    const itemsToAdd =
      tagGroupChoice === 'group'
        ? draftExportList
        : draftExportList.filter((item) => !item.isParent);
    const newItem = getNewItems(exportSelectedName, itemsToAdd, exportOrder);
    setExportSelectedName((prev) => {
      const existing = new Set(prev.map((x) => x.dataId));
      const merged = [...prev];
      newItem.forEach((i) => {
        if (!existing.has(i.dataId)) {
          merged.push(i);
          existing.add(i.dataId);
        }
      });
      return merged;
    });
    setExportOrder((prev) => {
      const existing = new Set(prev);
      const merged = [...prev];
      newItem.forEach((i) => {
        if (!existing.has(i.dataId)) {
          merged.push(i.dataId);
          existing.add(i.dataId);
        }
      });
      return merged;
    });
    if (options?.closeTagModal !== false) setShowTagModal(false);
  };

  const orderedExportItems = exportOrder
    .map((id) => exportSelectedName.find((x) => x.dataId === id))
    .filter(Boolean) as ExportSelectedNames[];

  const handleRemoveExportItem = (dataId: string) => {
    setExportSelectedName((prev) =>
      prev.filter((item) => item.dataId !== dataId)
    );
    setExportOrder((prev) => prev.filter((id) => id !== dataId));
    if (dataId.startsWith('m-')) {
      const metaId = dataId.replace('m-', '');
      setSelectedMetadataList((prev) =>
        prev.filter((m) => m.id.toString() !== metaId)
      );
      return;
    }
    const splitIndex = dataId.indexOf('-');
    if (splitIndex !== -1) {
      const wpGroup = dataId.substring(0, splitIndex);
      const wpIdRest = dataId.substring(splitIndex + 1);
      if (wpGroup === 'Tags' && wpIdRest.startsWith('taggroup-')) {
        const parentId = wpIdRest.replace('taggroup-', '');
        setTimeout(() => {
          processedParentTagsRef.current.delete(String(parentId));
        }, 50);
        const descendants = new Set(
          collectLeafDescendants(
            WORK_PRODUCT_TREE.data,
            'Tags',
            String(parentId)
          )
        );
        const tagLeafSet = new Set(WORK_PRODUCT_TREE.children?.['Tags'] ?? []);
        setSelectedWorkProduct((prev) => {
          const updated = {...prev};
          const current = (updated['Tags'] ?? []).map(String);
          updated['Tags'] = current.filter(
            (id) =>
              id !== String(parentId) &&
              !(tagLeafSet.has(id) && descendants.has(id))
          );
          return updated;
        });
        return;
      }
      const wpId = wpIdRest;
      setSelectedWorkProduct((prev) => {
        const updated = {...prev};
        const currentList = (updated[wpGroup] ?? []).map(String);
        updated[wpGroup] = removeUnneededParents(
          WORK_PRODUCT_TREE.data,
          wpGroup,
          currentList.filter((id) => id !== wpId) ?? []
        );
        return updated;
      });
    }
  };

  useEffect(() => {
    setSelectedCombinedData({
      metadata: selectedMetadataList,
      workProduct: selectedWorkProduct,
      aIGeneratedSummary: selectedAISummary,
    });
  }, [selectedMetadataList, selectedWorkProduct]);

  useEffect(() => {
    if (exportSelectedName.length === 0) {
      onRunStatusChange?.(false);
    } else {
      const savePayload = buildSaveReportPayload();
      onRunStatusChange?.(true, savePayload);
    }
  }, [
    exportSelectedName,
    exportOrder,
    filters,
    isReportShared,
    isToggleActive,
    isToggleScrubActive,
    addedWorkProduct,
  ]);
  useEffect(() => {
    if (isReset) {
      setActiveTab(0);
      setSelectedCombinedData({
        metadata: [],
        workProduct: {},
        aIGeneratedSummary: [],
      });
      setSelectedMetadataList([]);
      setSelectedWorkProduct({});
      setSelectedAISummary([]);
      setExportSelectedName([]);
      setExportOrder([]);
      setShowTagModal(false);
      setTagGroupQueue([]);
      setTagGroupQueueIndex(0);
      processedParentTagsRef.current = new Set();
    }
  }, [isReset]);

  const buildSelectionCriteria = (overrides?: {reportId?: number}) => {
    const normalizeSourceToIds = (
      source: string | string[] | number | number[] | undefined | null
    ): string[] => {
      if (source == null) return [];
      if (Array.isArray(source)) {
        return source
          .flatMap((v) => String(v).split(','))
          .map((v) => v.trim())
          .filter(Boolean);
      }
      return String(source)
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
    };
    const styleLabel = filters.style?.label ?? '.csv';
    const newLineLabel = filters.newLine?.label ?? ';';
    const textLabel = filters.text?.label ?? '"';
    const dateStyleLabel = filters.dateStyle?.label ?? 'MM/dd/yyyy';
    const field = filters.field?.label ?? ',';
    const buildFilterPayload = (filters: Record<string, FilterItem[]>) => {
      const sourceID: string[] = [];
      const sourceTypeSet = new Set<string>();
      Object.entries(filters).forEach(([key, filterArray]) => {
        filterArray.forEach(({sourceName, source}) => {
          if (key === 'sessionsearchexport') {
            const ids = normalizeSourceToIds(source as any);
            ids.forEach((id) => {
              if (!sourceID.includes(id)) sourceID.push(id);
            });
            // temp override: ensure backend sees session search context
            sourceTypeSet.add(SourceTypeCode.DOCLIST);
          } else {
            // existing behavior unchanged
            if (Array.isArray(source)) {
              sourceID.push(...source.map((s) => String(s)));
            } else if (source !== undefined && source !== null) {
              sourceID.push(String(source));
            }
            sourceTypeSet.add(sourceName);
          }
        });
      });
      return {
        sourceID,
        sourceType: Array.from(sourceTypeSet).join(', '),
      };
    };
    const getSelectedSource = (filter: any) => {
      const allowedKeys = [
        'tallyexport',
        'searchtermexport',
        'docexplorerexport',
        'timelineexport',
        'sessionsearchexport',
        'advbatmgmtrptexport',
        'securityGroups',
        'reviewers',
        'searches',
        'assignments',
        'projects',
        'folders',
        'doclistexport',
        'savedsearchexport',
        'assignmentexport',
      ];
      return allowedKeys.reduce((result, key) => {
        if (filter[key] !== undefined) {
          result[key] = filter[key];
        }
        return result;
      }, {} as any);
    };
    const selectedSource = getSelectedSource(filters);
    const values = buildFilterPayload(selectedSource);
    const ExportFields = exportOrder.map((dataId, index) => {
      const itemFound = exportSelectedName.find((x) => x.dataId === dataId);
      return {
        index,
        type:
          itemFound?.source === 'metadata'
            ? 'metadata'
            : itemFound?.source === 'aIGeneratedSummary'
              ? 'AIGeneratedSummary'
              : itemFound?.prefix === TAG_GROUP_EXPORT_TYPE
                ? TAG_GROUP_EXPORT_TYPE
                : singularizeWorkProduct(itemFound?.workProductKey ?? ''),
        fieldId: itemFound?.id?.toString() ?? '',
      };
    });
    const base = {
      sourceType: values?.sourceType,
      sourceID: values?.sourceID,
      style: styleLabel,
      field: field,
      text: textLabel,
      newLine: newLineLabel,
      dateStyle: dateStyleLabel,
      exportFields: ExportFields,
      exportData: [],
      scrubSpecialCharData:
        Object.keys(addedWorkProduct)?.includes('Comments') &&
        isToggleScrubActive
          ? finalScrubCharData?.length > 0
            ? finalScrubCharData
            : scrubCharData
          : [],
      isScrubSpecialCharacterAllow: Object.keys(addedWorkProduct)?.includes(
        'Comments'
      )
        ? isToggleScrubActive
        : false,
      isShared: isReportShared,
      isSavedSearchSource: true,
      toggelExportObjectValue: isToggleActive,
    } as const;
    if (typeof overrides?.reportId !== 'undefined') {
      (base as any).reportID = overrides?.reportId;
    }
    return base;
  };

  const buildRunReportPayload = () => ({
    selectionCriteria: buildSelectionCriteria({
      reportId: item?.id === '-25' ? 0 : item?.id,
    }),
    actionType: ActionType.ExportReport,
    reportType: item?.reportTypeCode ?? 'CDR011',
  });

  const buildSaveReportPayload = (params?: {
    reportId?: number;
    updateOrCreate?: boolean;
    sessionNameReportId?: number;
  }) => {
    const reportId = item?.id === '-25' ? 0 : item?.id;
    const selectionCriteria = buildSelectionCriteria({
      reportId,
    });
    const sessionNameReportId = params?.sessionNameReportId ?? 0;
    return {
      selectionCriteria,
      sessionNameReportId,
      actionType: ActionType.SaveReport,
      reportType: item?.reportTypeCode ?? 'CDR011',
      reportId,
    };
  };

  const handleRunReport = async () => {
    if (exportSelectedName.length === 0) return;
    const payload = buildRunReportPayload();
    try {
      setIsRunningReport(true);
      if (!filters || Object.keys(filters).length === 0) {
        triggerToast('Please select source', [ToastType.Error]);
        return;
      }
      runReport(payload, {
        onSuccess: (_data) => {
          triggerToast(t('reports.common.reportExportSuccess'), [
            ToastType.Success,
          ]);
          const savePayload = buildSaveReportPayload();
          onRunStatusChange?.(true, savePayload);
        },
        onError: (_err) => {
          triggerToast(t('reports.common.reportExportError'), [
            ToastType.Error,
          ]);
          onRunStatusChange?.(false);
        },
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_err) {
      triggerToast('Error occurred while running report', [ToastType.Error]);
    } finally {
      setIsRunningReport(false);
    }
  };

  return (
    <Grid container className="export-data-main">
      <Grid
        size={6}
        role="group"
        aria-labelledby="available-objects"
        className="available-objects"
      >
        <Box className="panel-header top-part">
          <Typography
            id="available-objects"
            className="panel-title"
            data-testid={`export-data-main-available-objects`}
          >
            {t('dataExportReport.availableObjects')}
          </Typography>
          <ArrButton
            size="small"
            onClick={handleAddToSelected}
            aria-label="Add selected items to export"
            endIcon={<ArrowForward />}
            disabled={
              selectedMetadataList.length === 0 &&
              selectedWorkProductTotalItem === 0 &&
              selectedAISummary.length === 0
            }
            className="add-to-selected-button"
            endIconSize="small"
            dataTestId="export-data-main-add-to-selected-button"
          >
            {t('dataExportReport.addToSelected')}
          </ArrButton>
        </Box>
        <Box
          role="tablist"
          aria-label="available-objects-tabs"
          className="tab-headings"
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            textColor="inherit"
            indicatorColor="primary"
            aria-label="Select available objects type"
            className="object-tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              label={
                selectedMetadataList.length > 0
                  ? t('dataExportReport.metadata', {
                      count: selectedMetadataList.length,
                    })
                  : t('dataExportReport.metadataWithoutCount')
              }
              {...a11yProps(0)}
              aria-controls="metadata-panel"
              className="object-tab"
            />
            <Tab
              label={
                selectedWorkProductTotalItem > 0
                  ? t('dataExportReport.workProduct', {
                      count: selectedWorkProductTotalItem,
                    })
                  : t('dataExportReport.workProductWithoutCount')
              }
              {...a11yProps(1)}
              aria-controls="work-product-panel"
              className="object-tab"
            />
            {aiSummaryKeyValue?.length > 0 && (
              <Tab
                label={
                  selectedAISummary.length > 0
                    ? t('dataExportReport.aiSummary', {
                        count: selectedAISummary.length,
                      })
                    : t('dataExportReport.aiSummaryWithoutCount')
                }
                {...a11yProps(2)}
                aria-controls="ai-summary-panel"
                className="object-tab"
              />
            )}
          </Tabs>
        </Box>
        <Box
          className={`available-objects-content ${activeTab === 0 ? 'metadata-mode' : ''}`}
          role="region"
        >
          {activeTab === 0 && (
            <Box
              role="group"
              aria-labelledby="metadata-group"
              className="tree-group"
            >
              <ArrTransferList
                showCheckbox={true}
                selectedList={selectedMetadataList}
                displayedList={metadataWithDisabled}
                onChange={handleMetadataCheckboxChange}
                checkboxDataTestId="export-data-metadata-checkbox"
              />
            </Box>
          )}

          {activeTab === 1 && (
            <Box role="group" aria-labelledby="work-product-group">
              {workProductTabDataWithDisabled.map((item, index) => (
                <Box
                  key={`m-${item.name}`}
                  role="group"
                  aria-labelledby={`tree-${item.name}`}
                  className="tree-group"
                >
                  <Typography
                    id={`tree-${item.name}`}
                    className="group-title"
                    data-testid={`export-data-main-name-${index}`}
                  >
                    {item.name}
                  </Typography>
                  <ArrTreeView
                    itemsList={item?.treedata}
                    multipleSelection
                    isShowCheckbox
                    isShowCountLabel={false}
                    isShowFileType={false}
                    treeSelectedItems={selectedWorkProduct[item.name] ?? []}
                    setSelectedItem={(data) =>
                      handleWorkProductSelection(item.name, data)
                    }
                    includeDisabledInSelectionState={true}
                    showTooltip={true}
                    dataTestId={item.name}
                  />
                </Box>
              ))}
            </Box>
          )}

          {activeTab === 2 && aiSummaryKeyValue?.length > 0 && (
            <Box
              role="group"
              aria-labelledby="aiSummary-group"
              className="tree-group"
            >
              <ArrTransferList
                showCheckbox={true}
                selectedList={selectedAISummary}
                displayedList={aiSummaryKeyValue}
                onChange={handleAISummaryChange}
                checkboxDataTestId="export-data-main-ai-summary-transfer-list-checkbox"
              />
            </Box>
          )}
        </Box>
      </Grid>
      <Grid
        size={6}
        role="region"
        aria-labelledby="selected-for-export-label"
        className="export-report-content"
      >
        <Box>
          <Typography
            id="selected-for-export-label"
            className="panel-title"
            data-testid={`export-data-main-selected-for-export`}
          >
            {t('dataExportReport.selectedForExport')}
          </Typography>
        </Box>
        <Box className="selected-panel">
          <Box className="selected-panel-wrapper">
            <Box className="selected-panel-header-left">
              <Box className="toggle-label-wrapper">
                <Typography
                  className="export-object-label"
                  data-testid={`export-data-main-export-object-names`}
                >
                  {t('dataExportReport.exportObjectNames')}{' '}
                </Typography>
                <ArrTooltipWrapper
                  title={t('dataExportReport.exportObjectNamesInfoTooltip')}
                  trigger={
                    <InfoIcon
                      tabIndex={0}
                      className="info-icon-label"
                      data-testid="toggle-info-tooltip"
                    />
                  }
                >
                  <Typography
                    className="typo-text label3_size"
                    data-testid="export-data-tooltip-title"
                  >
                    {t('dataExportReport.exportObjectNamesInfoTooltip')}
                  </Typography>
                  <Typography
                    className="typo-text body4_size"
                    data-testid="export-data-tooltip"
                  >
                    {t('dataExportReport.exportObjectNamesInfoTooltipContent')}
                  </Typography>
                </ArrTooltipWrapper>
              </Box>
              <Box className="toggle-button" data-testid="toggle-button">
                <ArrToggle
                  className="toggle-switch"
                  value={isToggleActive}
                  toggleSize="large"
                  onChange={(e) => handleActionToggle(e.target.checked)}
                  dataTestId="export-object"
                />
              </Box>
            </Box>
            {Object.keys(addedWorkProduct)?.includes('Comments') && (
              <Box className="selected-panel-header-left">
                <Box
                  className="toggle-label-wrapper"
                  onClick={handleShowScrubDrawer}
                  data-testid="export-data-main-box"
                >
                  <Typography
                    className="export-object-label1"
                    data-testid={`export-data-main-export-scrub-character`}
                  >
                    {t('dataExportReport.exportScrubCharacter')}{' '}
                  </Typography>
                  <ArrTooltipWrapper
                    title={t('dataExportReport.exportScrubCharacterInfo')}
                    trigger={
                      <InfoIcon
                        tabIndex={0}
                        className="info-icon-label"
                        data-testid="toggle-info-tooltip"
                      />
                    }
                  >
                    <Typography
                      className="typo-text body4_size"
                      data-testid="export-data-tooltip"
                    >
                      {t('dataExportReport.exportScrubCharacterInfo')}
                    </Typography>
                  </ArrTooltipWrapper>
                </Box>
                <Box className="toggle-button" data-testid="toggle-button">
                  <ArrToggle
                    value={isToggleScrubActive}
                    toggleSize="large"
                    onChange={(e) => handleActionScrubToggle(e.target.checked)}
                    dataTestId="action-edm-toggle"
                  />
                </Box>
              </Box>
            )}
          </Box>
          <ArrButton
            size="medium"
            onClick={handleRunReport}
            aria-label="Run export report"
            className="run-report-button"
            disabled={exportSelectedName?.length === 0 || isRunningReport}
            dataTestId="export-data-main-run-report-button"
          >
            {t('dataExportReport.runReport')}
          </ArrButton>
        </Box>
        <Box aria-live="polite" className="selected-panel-content">
          <ExportList
            items={orderedExportItems}
            onReorder={(newOrder) => setExportOrder(newOrder)}
            onRemoveItem={handleRemoveExportItem}
          />
        </Box>
      </Grid>
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={toastStatus}
          onClose={() => setShowToast(false)}
        />
      )}
      {showScrubDrawer && (
        <ArrDrawer
          size="small"
          side="right"
          transition={300}
          showButton={false}
          showHeader={false}
          isOpen={showScrubDrawer}
          className="redistribute-documents-drawer"
          ariaLabel="redistribute documents drawer"
          onClose={() => {}}
        >
          <ScrubCharacter
            fieldList={fieldList}
            replaceFieldList={replaceFieldList}
            scrubCharList={
              Object.keys(finalScrubCharData)?.length > 0
                ? finalScrubCharData
                : scrubCharData
            }
            onScrubCharListUpdate={handleScrubListUpdate}
            onCancel={handleOnCancel}
          />
        </ArrDrawer>
      )}
      {showTagModal && (
        <ArrModal
          showModal={showTagModal}
          headerTitle="Add TAG Group"
          className="tag-group-modal"
          onClose={handleTagModalCancel}
          dataTestId="tag-group-modal"
          buttons={[
            {
              id: 'cancel',
              label: 'Cancel',
              onClick: handleTagModalCancel,
              variant: 'secondary',
              size: 'small',
              dataTestId: 'tgm-cancel-button',
            },
            {
              id: 'ok',
              label: 'Ok',
              onClick: handleTagModalOk,
              variant: 'primary',
              size: 'small',
              dataTestId: 'tgm-ok-button',
            },
          ]}
        >
          <Box>
            <Typography
              id="arr-modal-description"
              className="typo-text body2_size desc-text-color"
              data-testid={`export-data-main-modal-popup-message`}
            >
              {"'" + (selectedTagGroupName ?? 'Selected Tag group Name') + "'"}{' '}
              {t('dataExportReport.modalPopupMessage')}
            </Typography>
            <Box className="tag-group-radio" mt={2}>
              <ArrRadio
                dataTestId={'modal-radio-button'}
                value={tagGroupChoice}
                onChange={(v: string) => setTagGroupChoice(v)}
                label=""
                name="tag-group-choice"
                data={[
                  {
                    label: t('dataExportReport.modalRadioButtonOne'),
                    value: 'group',
                  },
                  {
                    label: t('dataExportReport.modalRadioButtonTwo'),
                    value: 'individual',
                  },
                ]}
                className=""
              />
            </Box>
          </Box>
        </ArrModal>
      )}
    </Grid>
  );
};

export default ExportDataMain;
